import { lazy, Suspense, useEffect, useRef } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  useNavigate,
  useNavigationType,
} from 'react-router-dom';
import { scrollToSection } from './utils/scrollToSection';
import Spinner from './components/ui/Spinner';
import CookieConsent from './components/ui/CookieConsent';

// Lazy load pages for code splitting
const HomePage = lazy(() => import('./pages/HomePage'));
const ApplicationPage = lazy(() => import('./pages/ApplicationPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const PolicyPage = lazy(() => import('./pages/PolicyPage'));

const SECTION_TO_PATHNAME = {
  faq: '/faq',
  steps: '/krok-po-kroku',
  benefits: '/korzysci',
  contact: '/kontakt',
  branches: '/kontakt',
};

const ROUTE_TO_SECTION = {
  '/faq': 'faq',
  '/krok-po-kroku': 'steps',
  '/korzysci': 'benefits',
};

/**
 * Ensure route navigation starts at the top of the page.
 * React Router doesn't reset scroll on navigation by default.
 */
const ScrollToTopOnNavigate = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const previousPathname = useRef(location.pathname);

  useEffect(() => {
    const didPathnameChange = previousPathname.current !== location.pathname;
    previousPathname.current = location.pathname;
    if (!didPathnameChange) return;
    if (navigationType === 'POP') return;
    if (location.hash) return;
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname, location.hash, navigationType]);

  return null;
};

/**
 * Component to handle hash-based scrolling (e.g., /#faq)
 * Scrolls to section when hash is present in URL
 */
const ScrollToHash = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const hashSectionId = location.hash ? decodeURIComponent(location.hash.substring(1)) : null;
    const routeSectionId = !location.hash ? ROUTE_TO_SECTION[location.pathname] : null;
    const sectionId = hashSectionId || routeSectionId;

    if (!sectionId) return;

    let canceled = false;
    let attempts = 0;
    const maxAttempts = 40; // ~2s
    const retryDelayMs = 50;
    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const tryScroll = () => {
      if (canceled) return;

      const element = document.getElementById(sectionId);
      if (element) {
        scrollToSection(sectionId, {
          offset: 80,
          behavior: prefersReducedMotion ? 'auto' : 'smooth',
        });
        return;
      }

      attempts += 1;
      if (attempts < maxAttempts) {
        setTimeout(tryScroll, retryDelayMs);
        return;
      }

      const targetPathname = SECTION_TO_PATHNAME[sectionId];
      if (targetPathname && location.pathname !== targetPathname) {
        const isDedicatedSectionRoute = ROUTE_TO_SECTION[targetPathname] === sectionId;
        navigate(
          isDedicatedSectionRoute
            ? targetPathname
            : `${targetPathname}#${encodeURIComponent(sectionId)}`,
          { replace: true }
        );
      }
    };

    tryScroll();

    return () => {
      canceled = true;
    };
  }, [location.pathname, location.hash, navigate]);

  return null;
};

/**
 * Root App component with routing
 * Wraps pages with React Router and handles lazy loading
 */
const App = () => {
  return (
    <BrowserRouter>
      <ScrollToTopOnNavigate />
      <ScrollToHash />
      <Suspense
        fallback={
          <div className="min-h-screen flex items-center justify-center bg-bg-light">
            <Spinner />
          </div>
        }
      >
        <Routes>
          <Route path="/" element={<HomePage />} />
          {/* Section-friendly routes (render home and auto-scroll) */}
          <Route path="/faq" element={<HomePage />} />
          <Route path="/krok-po-kroku" element={<HomePage />} />
          <Route path="/korzysci" element={<HomePage />} />
          <Route path="/zloz-wniosek" element={<ApplicationPage />} />
          <Route path="/kontakt" element={<ContactPage />} />
          <Route path="/polityka-prywatnosci" element={<PolicyPage />} />
          {/* Fallback route - redirect to home */}
          <Route path="*" element={<HomePage />} />
        </Routes>
      </Suspense>
      {/* Cookie consent banner - appears on all pages */}
      <CookieConsent />
    </BrowserRouter>
  );
};

export default App;
